import * as THREE from 'three';
import { clamp } from '@/core/math/scalar';
import { CelestialBody } from '@/universe/CelestialBody';

/**
 * Tunable defaults shared by every material that derives its uniforms from
 * `CelestialBody.atmosphere` — `SkyMaterial`, `AtmosphereMaterial` and
 * `TerrainMaterial`. Before this was pulled out, the same handful of numbers
 * were re-typed independently in all three files; a tweak to one (say, the
 * sun's warm-white tint) silently stopped matching the other two.
 */

/** Warm-white sun colour, shared by every direct-light and scattering term. */
export const SUN_COLOR: readonly [number, number, number] = [1.0, 0.96, 0.9];

/**
 * Default sun irradiance multiplier. Not physically calibrated (there is no
 * real-world unit backing it); it is the value every body's lighting was
 * balanced against, so treat it as a fixed reference rather than a per-body
 * knob.
 */
export const SUN_INTENSITY_DEFAULT = 3;

/**
 * Rayleigh scale height floor, metres. Prevents a division blow-up (and a
 * degenerate, infinitely-thin shell) for a body authored with an
 * unrealistically small or zero scale height.
 */
export const MIN_SCALE_HEIGHT = 1;

/** Aerosol (Mie) density falls off much faster than molecular (Rayleigh) air. */
export const MIE_SCALE_HEIGHT_FRACTION = 0.22;

/**
 * `uDensityScale` clamp range and the surface density it is nominalised
 * against. Scattering coefficients are authored for a nominal sea-level
 * density of `DENSITY_SCALE_NOMINAL` kg/m3; dividing a body's actual surface
 * density by it and clamping keeps an outlier atmosphere (Ashkar's 58 kg/m3
 * soup, Vesper's near-vacuum) from driving the optical depth so far out of
 * range that the ray march either saturates to opaque white or contributes
 * nothing visible.
 */
export const DENSITY_SCALE_MIN = 0.25;
export const DENSITY_SCALE_MAX = 3.0;
export const DENSITY_SCALE_NOMINAL = 1.2;

// ---------------------------------------------------------------------------
// The scattering uniform block itself.
//
// The constants above stopped the *numbers* drifting between materials; the
// block below stops the uniforms themselves drifting. `SkyMaterial` and
// `AtmosphereMaterial` describe the same air with the same eleven uniforms,
// derived from `CelestialBody.atmosphere` by the same arithmetic, and each
// used to declare and derive its own copy — in GLSL and again in TypeScript.
// A body whose scale-height floor was applied in one and not the other lit its
// horizon differently from its sky.
//
// `AtmosphereMaterial` adds its own extras (Mie scale height, march step
// counts, exposure) on top of these; they are its alone and stay there.
// ---------------------------------------------------------------------------

/**
 * GLSL declarations for the shared scattering uniforms. Include near the top
 * of a fragment shader that uses `ScatteringUniforms`.
 */
export const GLSL_SCATTERING_UNIFORMS = /* glsl */ `
uniform vec3 uPlanetCentre;
uniform float uPlanetRadius;
uniform float uAtmosphereRadius;
uniform float uScaleHeight;
uniform vec3 uRayleigh;
uniform float uMie;
uniform float uMieG;
uniform vec3 uSunDirection;
uniform vec3 uSunColor;
uniform float uSunIntensity;
uniform float uDensityScale;
`;

/** TypeScript view of the uniforms `GLSL_SCATTERING_UNIFORMS` declares. */
export interface ScatteringUniforms {
  /** Planet centre relative to the camera, render units. */
  uPlanetCentre: THREE.IUniform<THREE.Vector3>;
  uPlanetRadius: THREE.IUniform<number>;
  uAtmosphereRadius: THREE.IUniform<number>;
  /** Rayleigh scale height, metres, floored at `MIN_SCALE_HEIGHT`. */
  uScaleHeight: THREE.IUniform<number>;
  uRayleigh: THREE.IUniform<THREE.Vector3>;
  uMie: THREE.IUniform<number>;
  /** Mie phase asymmetry, -1..1. */
  uMieG: THREE.IUniform<number>;
  uSunDirection: THREE.IUniform<THREE.Vector3>;
  uSunColor: THREE.IUniform<THREE.Color>;
  uSunIntensity: THREE.IUniform<number>;
  /** Surface density nominalised and clamped — see `DENSITY_SCALE_NOMINAL`. */
  uDensityScale: THREE.IUniform<number>;
}

/** Builds the shared scattering uniforms for one body. */
export function createScatteringUniforms(body: CelestialBody): ScatteringUniforms {
  const atm = body.atmosphere;
  return {
    uPlanetCentre: { value: new THREE.Vector3() },
    uPlanetRadius: { value: body.radius },
    uAtmosphereRadius: { value: body.radius + body.atmosphereTop },
    uScaleHeight: { value: Math.max(MIN_SCALE_HEIGHT, atm.scaleHeight) },
    uRayleigh: { value: new THREE.Vector3(atm.rayleigh[0], atm.rayleigh[1], atm.rayleigh[2]) },
    uMie: { value: atm.mie },
    uMieG: { value: atm.mieAnisotropy },
    uSunDirection: { value: new THREE.Vector3(1, 0, 0) },
    uSunColor: { value: new THREE.Color(...SUN_COLOR) },
    uSunIntensity: { value: SUN_INTENSITY_DEFAULT },
    // Scattering coefficients are authored at a nominal sea-level density, so
    // an unusually thick or thin atmosphere scales the path length rather than
    // the colour: Ashkar's 58 kg/m3 soup is opaque, Vesper's wisp is not.
    uDensityScale: {
      value: clamp(atm.surfaceDensity / DENSITY_SCALE_NOMINAL, DENSITY_SCALE_MIN, DENSITY_SCALE_MAX),
    },
  };
}
